import { Injectable } from '@angular/core';
import {
  switchMap,
  map,
  startWith,
  last,
  tap,
  takeUntil
} from 'rxjs/operators';
import { Observable, fromEvent, throwError, combineLatest } from 'rxjs';
import * as crypto from 'crypto-js';

export interface UploadProgressSnapShot {
  progress: number;
  completed: boolean;
  downloadUrl: string;
  filename: string;
  originalFilename: string;
  id: string;
}

export interface FileLoadProgressSnapShot {
  filename: string;
  type: string;
  size: number;
  progress: number;
  file: File;
  binary: any;
  completed: boolean;
  md5: string;
}

@Injectable({ providedIn: 'root' })
export class UploadService {
  public files = new Map<string, { file: File; binary: any }>();

  public openUploadDialog(
    basePath: string,
    md5 = true,
    accept = '**/**'
  ): Observable<UploadProgressSnapShot> {
    const fileInput = document.createElement('input');
    fileInput.setAttribute('type', 'file');
    fileInput.setAttribute('style', 'display: none');
    fileInput.setAttribute('accept', accept);
    document.body.appendChild(fileInput);
    const change$ = fromEvent(fileInput, 'change').pipe(
      map((event: any) => {
        document.body.removeChild(fileInput);
        const files: { [key: string]: File } = event.target.files as any;
        if (Object.keys(files).length !== 1) {
          throw new Error('File upload failed');
        }
        let file: File;
        for (const key in files) {
          if (!isNaN(parseInt(key, 10))) {
            file = files[key];
          }
        }
        if (!file) {
          throw new Error('File upload failed');
        }
        return file;
      }),
      switchMap(file => this.upload(basePath, file, md5))
    );
    fileInput.click();
    return change$;
  }

  public stageFileUpload(
    accept = '**/**'
  ): Observable<FileLoadProgressSnapShot> {
    const fileInput = document.createElement('input');
    fileInput.setAttribute('type', 'file');
    fileInput.setAttribute('style', 'display: none');
    fileInput.setAttribute('accept', accept);
    document.body.appendChild(fileInput);

    const change$ = fromEvent(fileInput, 'change').pipe(
      map((event: any) => {
        const files: { [key: string]: File } = event.target.files as any;
        if (Object.keys(files).length !== 1) {
          throw new Error('File upload failed');
        }
        let file: File;
        for (const key in files) {
          if (!isNaN(parseInt(key, 10))) {
            file = files[key];
          }
        }
        if (!file) {
          throw new Error('File upload failed');
        }
        return file;
      }),

      switchMap(file => {
        const reader = new FileReader();

        const load$ = fromEvent<ProgressEvent>(reader, 'load').pipe(
          map(event => {
            const binary = (event.target as any).result;
            const md5 = crypto.MD5(binary).toString();

            this.files.set(md5, { file, binary });

            return { binary, md5 };
          })
        );

        const progress$ = fromEvent<ProgressEvent>(reader, 'progress').pipe(
          map(event => (event.loaded || 0) / (event.total || 1))
        );

        const error$ = fromEvent<ProgressEvent>(reader, 'error');

        const loaded$ = fromEvent(reader, 'loaded');

        reader.readAsBinaryString(file);

        return combineLatest(
          load$.pipe(startWith(null)),
          progress$.pipe(startWith(null)),
          error$.pipe(startWith(null))
        ).pipe(
          takeUntil(loaded$),
          map(([load, progress, error]) => {
            if (error) {
              throw new Error(`File loading failed for '${file.name}'`);
            }

            return {
              filename: file.name,
              type: file.type,
              size: file.size,
              progress: progress,
              file,
              binary: load ? load.binary : null,
              completed: !!load,
              md5: load ? load.md5 : null
            };
          })
        );
      })
    );

    fileInput.click();
    document.body.removeChild(fileInput);
    return change$;
  }

  public upload(
    basePath: string,
    file: File,
    md5 = true
  ): Observable<UploadProgressSnapShot> {
    return throwError('[UploadService::upload] Not implemented');
  }

  public uploadFromFile(
    filename: string,
    basePath: string
  ): Observable<UploadProgressSnapShot> {
    return throwError('[UploadService::uploadFromFile] Not implemented');
  }
}
